// ==================================================================================
// Digital Approval System (DAS) - ChartCard
// ==================================================================================
// Visual wrapper around a single chart / table widget on a report page.
// Renders a card with a title bar; the chart/table lives in the content slot.
// ==================================================================================

import React from 'react';

export interface ChartCardProps {
  title: string;
  subtitle?: React.ReactNode;
  height?: number | string;
  children: React.ReactNode;
  className?: string;
}

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

const titleStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#1f2937',
  margin: 0,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#6b7280',
  margin: 0,
};

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  height,
  children,
  className,
}) => {
  // height="100%" → fill parent vertically via flex. Otherwise use the given
  // numeric / "auto" value directly.
  const fillsParent = height === '100%';
  const contentStyle: React.CSSProperties = fillsParent
    ? { width: '100%', flex: 1, minHeight: 0 }
    : { width: '100%', height: height ?? 280 };
  const outerStyle: React.CSSProperties = fillsParent
    ? { ...cardStyle, flex: 1, minHeight: 0 }
    : cardStyle;

  return (
    <div style={outerStyle} className={className}>
      <div>
        <h3 style={titleStyle}>{title}</h3>
        {/* subtitle is rendered in a <div> rather than a <p> because callers
            may pass complex React nodes (e.g. a wrapper <div> containing a
            checkbox toggle). HTML parsers auto-close <p> before encountering
            a block element, which mangles the DOM and causes layout/scroll
            glitches inside the card. */}
        {subtitle && <div style={subtitleStyle}>{subtitle}</div>}
      </div>
      <div style={contentStyle}>{children}</div>
    </div>
  );
};

export default ChartCard;
